import { AIAgentAnalyticsItemModel } from "@jugl-web/rest-api/integrations/models/AIAgentAnalyticsItem/AIAgentAnalyticsItemModel";
import { colord } from "colord";
import Highcharts from "highcharts";
import { useMemo } from "react";
import { TopicDistributionDataEntry } from "../components/TopicDistributionWidget";
import { getBaseColorByIndex } from "../utils";

interface UseTopicDistributionWidgetDataOptions {
  analyticsData: AIAgentAnalyticsItemModel[];
}

/** Max subtopic rows per topic. Remaining low-count subtopics roll into "Other" */
const MAX_SUBTOPICS_PER_TOPIC = 10;

export const useTopicDistributionWidgetData = ({
  analyticsData,
}: UseTopicDistributionWidgetDataOptions) => {
  const topicDistributionData = useMemo<TopicDistributionDataEntry[]>(() => {
    const dict = analyticsData.reduce<Record<string, Record<string, number>>>(
      (acc, item) => {
        acc[item.topic] = acc[item.topic] ?? {};
        acc[item.topic][item.subtopic] =
          (acc[item.topic][item.subtopic] ?? 0) + 1;

        return acc;
      },
      {}
    );

    const data: TopicDistributionDataEntry[] = [];

    Object.entries(dict).forEach(([topic, subtopics], index) => {
      const baseColor = getBaseColorByIndex(index);

      const displaySubtopic = getDisplaySubtopics(subtopics);
      const displaySubtopicValues = displaySubtopic.map(({ value }) => value);

      const minSubtopicValue = Math.min(...displaySubtopicValues);
      const maxSubtopicValue = Math.max(...displaySubtopicValues);

      data.push({
        type: "topic",
        name: topic,
        color: baseColor,
      });

      displaySubtopic.forEach((subtopic) => {
        const valueBasedColor = getValueBasedSubtopicColor(
          baseColor,
          subtopic.value,
          minSubtopicValue,
          maxSubtopicValue
        );

        data.push({
          type: "subtopic",
          name: subtopic.name,
          parent: topic,
          value: subtopic.value,
          chatsCount: subtopic.chatsCount,
          color: valueBasedColor,
        });
      });
    });

    return data;
  }, [analyticsData]);

  return topicDistributionData;
};

/**
 * Maps subtopic value to a color shade: higher values are darker.
 * Uses the midpoint if all values are equal.
 */
const getValueBasedSubtopicColor = (
  baseColor: string,
  value: number,
  minValue: number,
  maxValue: number
): string => {
  const hsl = colord(baseColor).toHsl();

  hsl.l = 90;
  const lightColor = colord(hsl).toHex();

  hsl.l = 30;
  const darkColor = colord(hsl).toHex();

  const t =
    minValue === maxValue ? 0.5 : (value - minValue) / (maxValue - minValue);

  const blendedColorType = Highcharts.color(lightColor).tweenTo(
    Highcharts.color(darkColor),
    t
  );

  return Highcharts.color(blendedColorType).get("rgb").toString();
};

/** Returns top subtopics by count, merging extras into a single "Other" entry */
const getDisplaySubtopics = (
  subtopics: Record<string, number>
): { name: string; value: number; chatsCount: number }[] => {
  const sortedDesc = Object.entries(subtopics).sort((a, b) => b[1] - a[1]);

  if (sortedDesc.length <= MAX_SUBTOPICS_PER_TOPIC) {
    return sortedDesc.map(([name, value]) => ({
      name,
      value,
      chatsCount: value,
    }));
  }

  const keptSubtopicsCount = MAX_SUBTOPICS_PER_TOPIC - 1;

  const keptSubtopics = sortedDesc
    .slice(0, keptSubtopicsCount)
    .map(([name, value]) => ({
      name,
      value,
      chatsCount: value,
    }));

  const otherTotalSubtopicsCount = sortedDesc
    .slice(keptSubtopicsCount)
    .reduce((sum, [, count]) => sum + count, 0);

  return [
    ...keptSubtopics,
    {
      name: "Other",
      value: keptSubtopics[keptSubtopics.length - 1].value,
      chatsCount: otherTotalSubtopicsCount,
    },
  ];
};
